import { format, parse, parseISO } from 'date-fns';
import ContactNote, { ContactNoteDialog, ContactNoteDialogSubmission } from 'types/ContactNote';
import { MultiplePartnerDetailType, PartnerDetailType } from 'types/LegalPartner';
import { UserPartnerType } from 'views/ContactNoteFormDialog/zafire/components/data/UserPartnerStatus';

//Zafire is using separate date and time, merge them back to dateTime parameter of existing API

export const convertDateObject = (note: ContactNoteDialog) => {
  const { startDate, startTime, endDate, endTime } = note;

  const startDateTime = startDate
    ? startTime
      ? format(
          parse(
            `${format(new Date(startDate), 'yyyy-MM-dd')} ${format(startTime, 'HH:mm')}`,
            'yyyy-MM-dd HH:mm',
            new Date(),
          ),
          "yyyy-MM-dd'T'HH:mm:ssXXX",
        )
      : format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ssXXX")
    : '';

  const endDateTime =
    endDate && endTime
      ? format(
          parse(
            `${format(new Date(endDate), 'yyyy-MM-dd')} ${format(endTime, 'HH:mm')}`,
            'yyyy-MM-dd HH:mm',
            new Date(),
          ),
          "yyyy-MM-dd'T'HH:mm:ssXXX",
        )
      : '';

  const updatedNote = { ...note, startDateTime, endDateTime, isTimeAllowed: !startTime };
  return updatedNote;
};

export const revertDateObject = (note: ContactNote) => {
  const { startDateTime, endDateTime, isTimeAllowed } = note;

  let startDate = '';
  let startTime = '';
  let endDate = '';
  let endTime = '';

  if (startDateTime) {
    const parsedStart = parseISO(startDateTime).toISOString();
    startDate = parsedStart;

    if (!isTimeAllowed) {
      startTime = parsedStart;
    }
  }

  if (endDateTime) {
    const parsedEnd = parseISO(endDateTime).toISOString();
    endDate = parsedEnd;
    endTime = parsedEnd;
  }

  return {
    ...note,
    startDate,
    startTime,
    endDate,
    endTime,
  };
};

export const serialiseData = (note: ContactNoteDialog) => {
  const data = convertDateObject(note);
  const { relatedBRs, relatedPartners, additionalAttendees, attachments } = data;
  const submissionPayload = {
    ...data,
    relatedBRs: relatedBRs.filter((item) => item !== 'other').map((item) => ({ relatedBrUid: item })),
    relatedPartners: relatedPartners
      .filter((item) => item.value !== 'other')
      .map((item) => ({ partnerUId: item.value })),
    additionalAttendees: additionalAttendees.map((item) => ({ userId: item })),
    attachments: attachments.map((item) => {
      if (item.b64Data) {
        return { description: item.description, name: item.name, b64Data: item.b64Data, file: item.file };
      }
      return item;
    }),
  };
  return submissionPayload;
};

export type relatedPartnerItem = { value: string; type?: string };

export type DeserialisedContactNote = Omit<ContactNote, 'relatedPartners' | 'relatedBRs' | 'additionalAttendees'> & {
  relatedBRs: string[];
  relatedPartners: relatedPartnerItem[];
  otherPartner: string;
  additionalAttendees: string[];
};
export const deserialiseData = (note: ContactNote): DeserialisedContactNote => {
  const data = revertDateObject(note);
  return {
    ...data,
    relatedBRs: (data as ContactNoteDialogSubmission)?.relatedBRs?.map((item) => item.relatedBrUid) ?? [],
    relatedPartners:
      (data as ContactNoteDialogSubmission)?.relatedPartners?.map((item) => ({
        value: item.partnerUId,
        type: undefined,
      })) ?? [],
    otherPartner: data?.otherPartner || '',
    additionalAttendees: (data as ContactNoteDialogSubmission)?.additionalAttendees?.map((item) => item.userId) ?? [],
  };
};

export const isBRFieldMandatoryCheck = (
  partnerDetails: MultiplePartnerDetailType[] | PartnerDetailType | undefined,
) => {
  if (Array.isArray(partnerDetails)) {
    const result = partnerDetails.find((item) => {
      if (item.partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
        return item.partnerDetails.individualPartner.status === UserPartnerType.ACTIVE;
      } else if (item.partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
        return item.partnerDetails.legalPartner.status === UserPartnerType.ACTIVE;
      }
    });
    return result !== undefined;
  } else if (partnerDetails) {
    if (partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
      return partnerDetails.individualPartner.status === UserPartnerType.ONBOARDED;
    } else if (partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
      return partnerDetails.legalPartner.status === UserPartnerType.ONBOARDED;
    }
  }
  return false;
};

export const decodeBase64ToBlob = async (base64: string): Promise<Blob> => {
  const byteCharacters = atob(base64);
  const byteArrays: BlobPart[] = [];

  const chunkSize = 512;
  const totalChunks = Math.ceil(byteCharacters.length / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const slice = byteCharacters.slice(i * chunkSize, (i + 1) * chunkSize);

    const byteNumbers = Array.from(slice, (char) => char.charCodeAt(0));
    byteArrays.push(new Uint8Array(byteNumbers));

    if (i % 100 === 0) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  return new Blob(byteArrays);
};
